import { ChevronRight, RotateCcw, X } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { callWasNotPlaced } from "./callError";
import type { CallState } from "./callState";
import { anySkipped, nextBlocked, queueLast, queueProgress, type CallQueue } from "./queue/queue";
import type { CallOutcomeApi } from "./queue/useCallOutcome";

/**
 * The footer of a call that is over: what to do next.
 *
 * Two shapes, and the difference is whether there is a list behind this call.
 * On a single call it is what it always was - call again, close. On a run it is
 * the wrap-up: where the list is, what this call came to, and the button that
 * dials the next number without going back to the table to find it.
 *
 * The wrap-up is what makes the run worth having. Pressing "next" with the last
 * conversation still fresh takes one click; remembering it at six o'clock takes
 * a guess, which is why the outcome is asked for HERE and why on a run the next
 * number waits for it - see `nextBlocked`, which also says where that rule
 * deliberately gives way.
 *
 * "Call again" only appears when there is something to call again. An inbound
 * call that ended carries no target, and a button that redialled whatever was
 * last in the pad would be worse than no button.
 */
export function EndOfCall({
  state,
  queue,
  outcome,
  onRedial,
  onNext,
  onStopQueue,
  onDismiss,
}: {
  state: CallState;
  /** The run this call belongs to, or null on a single call. */
  queue: CallQueue | null;
  outcome: CallOutcomeApi;
  onRedial: () => void;
  onNext: () => void;
  onStopQueue: () => void;
  onDismiss: () => void;
}) {
  const t = useT();
  /**
   * There is a number, and pressing it again would change nothing.
   *
   * The do-not-call refusal is the case that made this worth a condition of its
   * own: the window said "somebody asked us to stop ringing them" and offered
   * "Call again" directly underneath, which is the app arguing with its own
   * register. The same is true of a number Twilio does not recognise and of one
   * that is not dialable at all - the second attempt fails in exactly the same
   * place as the first. Everything else keeps the button, including a blocked
   * microphone: that one really can be fixed and tried again.
   */
  // Nor after a takeover: the target is the AI's call, and dialling it again
  // from here would be a cold call from this browser that nobody asked for.
  const canRedial = !!state.target && !state.takeover && !callWasNotPlaced(state.error);

  if (!queue) {
    return (
      <div className="mt-4 flex flex-none gap-2.5">
        {canRedial ? (
          <button type="button" className="crm-btn flex-1 justify-center" onClick={onRedial}>
            <RotateCcw className="size-4" />
            {t("calls.modal.callAgain")}
          </button>
        ) : null}
        <button
          type="button"
          className="crm-btn crm-btn-dark flex-1 justify-center"
          onClick={onDismiss}
        >
          <X className="size-4" />
          {t("calls.modal.close")}
        </button>
      </div>
    );
  }

  const { position, total } = queueProgress(queue);
  const last = queueLast(queue);
  const blocked = nextBlocked({
    labels: outcome.labels,
    tags: outcome.tags,
    callLogId: state.callLogId,
  });

  return (
    <>
      <div className="mt-5 w-full flex-none">
        <div className="flex items-baseline justify-between">
          <span className="crm-qsect">{t("calls.queue.title")}</span>
          <span className="text-[12.5px] font-bold tabular-nums">
            {t("calls.queue.progress", { position, total })}
          </span>
        </div>
        <span className="crm-qbar" aria-hidden>
          <i style={{ width: `${Math.round((position / total) * 100)}%` }} />
        </span>
        {/* Once, on the first call of the run. It describes the whole list, and
            repeating it after every call would be the same sentence seventeen
            times about something that cannot change. */}
        {queue.cursor === 0 && anySkipped(queue.skipped) ? (
          <p className="crm-sub mt-2 text-[12px]">
            {[
              queue.skipped.invalid > 0 && t("calls.queue.skipInvalid", { n: queue.skipped.invalid }),
              queue.skipped.suppressed > 0 &&
                t("calls.queue.skipSuppressed", { n: queue.skipped.suppressed }),
              queue.skipped.duplicate > 0 &&
                t("calls.queue.skipDuplicate", { n: queue.skipped.duplicate }),
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        ) : null}
      </div>

      {/* The reason sits above the button rather than inside a tooltip on it: a
          disabled control the operator cannot hover on a touch screen is a
          control with no explanation at all. */}
      {blocked && !last ? (
        <p className="crm-sub mt-2.5 w-full flex-none text-[12px] font-[600]">
          {t("calls.queue.needOutcome")}
        </p>
      ) : null}

      <div className="mt-4 flex flex-none gap-2.5">
        {last ? null : (
          <button
            type="button"
            className="crm-btn crm-btn-brand flex-1 justify-center"
            onClick={onNext}
            disabled={!!blocked}
          >
            {t("calls.queue.next")}
            <ChevronRight className="size-4" />
          </button>
        )}
        {/* Icon only, and it keeps the run: a redial dials the same number
            again without moving the cursor, which is what an operator who
            reached a voicemail wants and the only way to get it. */}
        {canRedial ? (
          <button
            type="button"
            className="crm-btn crm-btn-r"
            onClick={onRedial}
            title={t("calls.modal.callAgain")}
            aria-label={t("calls.modal.callAgain")}
          >
            <RotateCcw className="size-4" />
          </button>
        ) : null}
        <button
          type="button"
          className={cn("crm-btn justify-center", last ? "crm-btn-brand flex-1" : "crm-btn-dark")}
          onClick={onStopQueue}
          title={last ? undefined : t("calls.queue.stopHint")}
        >
          <X className="size-4" />
          {last ? t("calls.queue.finish") : t("calls.queue.stop")}
        </button>
      </div>
    </>
  );
}
